import { Link } from "react-router-dom";
import { Phone, Facebook, Instagram, Youtube, Linkedin } from "lucide-react";
import { Logo, EqualHousing } from "./Badges";
import { NAV_LINKS, SOCIALS, PHONE_DISPLAY, TEL_HREF, EMAIL, MAILTO, NMLS_URL } from "@/data/content";

const SOCIAL_ITEMS = [
  { key: "facebook", label: "Facebook", href: SOCIALS.facebook, Icon: Facebook },
  { key: "instagram", label: "Instagram", href: SOCIALS.instagram, Icon: Instagram },
  { key: "youtube", label: "YouTube", href: SOCIALS.youtube, Icon: Youtube },
  { key: "linkedin", label: "LinkedIn", href: SOCIALS.linkedin, Icon: Linkedin },
];

export default function Footer() {
  return (
    <footer className="bg-navy text-mist" data-testid="footer">
      <div className="wrap pt-16 pb-28 md:pb-10">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.3fr_0.8fr_1.1fr]">
          <div>
            <Logo transparent className="h-12 w-auto" />
            <p className="mt-6 max-w-sm text-sm leading-relaxed">
              Honest guidance, competitive rates, and a personal touch that big banks simply cannot match.
            </p>
            <div className="mt-7 flex items-center gap-3">
              {SOCIAL_ITEMS.map(({ key, label, href, Icon }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${key}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-mist transition-all duration-200 hover:-translate-y-0.5 hover:border-gold hover:text-gold"
                >
                  <Icon size={16} strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-gold">Quick Links</p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="transition-colors duration-150 hover:text-gold" data-testid={`footer-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-gold">Contact</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a href={TEL_HREF} data-testid="footer-phone" className="inline-flex items-center gap-2 transition-colors duration-150 hover:text-gold">
                  <Phone size={14} strokeWidth={1.75} /> {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a href={MAILTO} data-testid="footer-email" className="break-all transition-colors duration-150 hover:text-gold">
                  {EMAIL}
                </a>
              </li>
              <li className="leading-relaxed">6185 Deltona Blvd, Spring Hill, FL 34606</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 space-y-5 border-t border-white/10 pt-8">
          <p className="text-xs leading-relaxed">
            Family First Mortgage USA, empowered by NEXA Lending. Branch NMLS #2729829 | Personal NMLS #2142845.{" "}
            <a
              href={NMLS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-nmls-link"
              className="text-gold underline underline-offset-2 transition-colors hover:brightness-110"
            >
              NMLS Consumer Access
            </a>
          </p>
          <div className="flex items-center gap-4 text-xs leading-relaxed">
            <EqualHousing className="h-9 w-9 shrink-0 text-mist" />
            <p>
              Equal Housing Opportunity. We do business in accordance with the Federal Fair Housing Law and the Equal
              Credit Opportunity Act.
            </p>
          </div>
          <div className="flex flex-col gap-3 text-xs text-mist/80 sm:flex-row sm:items-center sm:justify-between">
            <Link to="/privacy" data-testid="footer-privacy" className="transition-colors hover:text-gold">
              Privacy Policy
            </Link>
            <p>© 2026 Family First Mortgage USA.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
